import { Component, OnInit, OnDestroy, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ContractsService } from '../../../services/contracts.service';
import { B2bContractDetail } from '../../../models/Contract';
import { ErrorService } from '../../../shared/services/error.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { TableAction, TableColumn } from '../../../shared/components/table/table.component';
import { B2B_EXPORT_PAGE_SIZE, exportB2bTableToExcel, rowsFromPagedResponse } from '../../../utils/b2b-table-export.util';
import { PaymentService } from '../../../services/payment.service';
import { AccountsReceivableService } from '../../../services/accounts-receivable.service';
import { AccountsReceivable } from '../../../models/AccountsReceivable';
import { PDFDocument } from 'pdf-lib';
import {
  ContractCancelReason,
  CONTRACT_CANCEL_REASON_SELECT_OPTIONS,
  contractCancelMotivoFromCode
} from '../../../constants/contract-cancel-reasons';

@Component({
  selector: 'app-b2b-contracts-view',
  templateUrl: './b2b-contracts-view.component.html',
  styleUrls: ['./b2b-contracts-view.component.css']
})
export class B2bContractsViewComponent implements OnInit, OnDestroy {
  @ViewChild('signaturePdfCanvas') signaturePdfCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('docxPreviewContainer') docxPreviewContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('b2bSignatureCanvas') b2bSignatureCanvasRef?: ElementRef<HTMLCanvasElement>;

  readonly managementMenu: Array<{
    title: string;
    items: Array<{
      key: string;
      label: string;
      icon: string;
      primary?: boolean;
      tone: string;
      type: 'scroll' | 'route' | 'action';
      target?: string;
    }>;
  }> = [
    {
      title: 'Contrato',
      items: [
        { key: 'detalhes', label: 'Detalhes', icon: 'fas fa-file-alt', tone: 'blue', type: 'scroll', target: 'detalhes-contrato' },
        { key: 'pedidos', label: 'Pedidos', icon: 'fas fa-clipboard-list', tone: 'sky', type: 'route', target: 'items' },
        { key: 'frota', label: 'Frota', icon: 'fas fa-motorcycle', tone: 'teal', type: 'route', target: 'vehicles' },
        { key: 'condutores', label: 'Condutores', icon: 'fas fa-id-card', tone: 'cyan', type: 'route', target: 'drivers' },
        { key: 'central', label: 'Central de Controle', icon: 'fas fa-users-cog', primary: true, tone: 'indigo', type: 'route', target: 'central-controle' },
        { key: 'portal-users', label: 'Usuários do portal', icon: 'fas fa-user-shield', tone: 'slate', type: 'route', target: 'portal-users' }
      ]
    },
    {
      title: 'Operacional',
      items: [
        { key: 'ops-dash', label: 'Dashboard operacional', icon: 'fas fa-chart-pie', primary: true, tone: 'indigo', type: 'route', target: 'operacional' },
        { key: 'manutencoes', label: 'Manutenções / OS', icon: 'fas fa-wrench', tone: 'amber', type: 'route', target: 'manutencoes' },
        { key: 'sinistros', label: 'Sinistros', icon: 'fas fa-car-crash', tone: 'rose', type: 'route', target: 'sinistros' },
        { key: 'multas', label: 'Multas', icon: 'fas fa-gavel', tone: 'orange', type: 'route', target: 'multas' },
        { key: 'cobrancas', label: 'Cobranças', icon: 'fas fa-file-invoice-dollar', tone: 'violet', type: 'route', target: 'cobrancas' },
        { key: 'faturas', label: 'Faturas', icon: 'fas fa-file-invoice', tone: 'violet', type: 'route', target: 'faturas' },
        { key: 'reservas', label: 'Reservas', icon: 'fas fa-calendar-check', tone: 'sky', type: 'route', target: 'reservas' },
        { key: 'substituicoes', label: 'Substituições', icon: 'fas fa-exchange-alt', tone: 'teal', type: 'route', target: 'substituicoes' },
        { key: 'retornos', label: 'Retornos', icon: 'fas fa-undo', tone: 'slate', type: 'route', target: 'retornos' },
        { key: 'entregas', label: 'Entregas', icon: 'fas fa-truck', tone: 'cyan', type: 'route', target: 'entregas' },
        { key: 'acessorios', label: 'Acessórios', icon: 'fas fa-toolbox', tone: 'indigo', type: 'route', target: 'acessorios' },
        { key: 'pagamentos', label: 'Pagamentos', icon: 'fas fa-money-check-alt', tone: 'green', type: 'route', target: 'pagamentos' }
      ]
    },
    {
      title: 'Documentos e financeiro',
      items: [
        { key: 'docs', label: 'Documentos', icon: 'fas fa-file-pdf', tone: 'violet', type: 'scroll', target: 'documentos-contrato' },
        { key: 'financeiro', label: 'Situação financeira', icon: 'fas fa-coins', tone: 'green', type: 'scroll', target: 'financeiro-contrato' },
        { key: 'fechamento', label: 'Espelho / Fechamento', icon: 'fas fa-balance-scale', tone: 'indigo', type: 'action', target: 'billing-closing' },
        { key: 'itens-pagina', label: 'Itens nesta página', icon: 'fas fa-list', tone: 'blue', type: 'action', target: 'show-items' },
        { key: 'caucao', label: 'Devolução de caução', icon: 'fas fa-undo-alt', tone: 'amber', type: 'action', target: 'deposit-refund' }
      ]
    }
  ];
  @ViewChild('documentViewerDocxContainer') documentViewerDocxContainer?: ElementRef<HTMLDivElement>;
  contract: B2bContractDetail | null = null;
  isLoading = false;
  contractId: number | null = null;
  
  // Visualização de Itens (sempre visível na tela de visualizar contrato)
  showItemsTable = true;
  items: any[] = [];
  isLoadingItems = false;
  totalItems = 0;
  itemsPage = 1;
  itemsPageSize = 10;
  itemsTotalPages = 0;
  itemsSearchTerm = '';

  // Lista de condutores do contrato (visível dentro da view)
  drivers: any[] = [];
  isLoadingDrivers = false;
  totalDrivers = 0;
  driversPage = 1;
  driversPageSize = 10;
  driversSearchTerm = '';
  driversTotalPages = 0;

  // Frota (snapshots) paginada na visualização do contrato
  vehicles: any[] = [];
  isLoadingVehicles = false;
  totalVehicles = 0;
  vehiclesPage = 1;
  vehiclesPageSize = 10;
  vehiclesSearchTerm = '';
  vehiclesTotalPages = 0;

  // Situação financeira do contrato (contas a receber internas)
  receivables: AccountsReceivable[] = [];
  receivablesTotal = 0;
  isLoadingReceivables = false;

  receivablesColumns: TableColumn[] = [
    { titulo: 'Documento', label: 'documentNumber' },
    { titulo: 'Descrição', label: 'description' },
    { titulo: 'Parcela', label: 'installmentLabel' },
    { titulo: 'Vencimento', label: 'dueDate', formatter: (value: any) => this.formatDate(value) },
    { titulo: 'Valor', label: 'amountToReceive', formatter: (value: any) => this.formatCurrency(Number(value) || 0), sensitive: true },
    { titulo: 'Status', label: 'status' }
  ];

  // Configuração da tabela de pedidos (itens do contrato)
  itemsColumns = [
    { titulo: 'Número do Pedido', label: 'itemNumber' },
    { titulo: 'Tipo de Moto', label: 'motorcycleType' },
    { titulo: 'Quantidade', label: 'quantity' },
    { titulo: 'Valor Unitário', label: 'unitValue', formatter: (value: any) => this.formatCurrency(value), sensitive: true },
    { titulo: 'Tipo de Pagamento', label: 'paymentType' },
    { titulo: 'Status', label: 'status', formatter: (value: any) => this.getStatusLabel(value) },
  ];

  // Configuração da tabela de condutores (lista embutida na view)
  driverColumns: TableColumn[] = [
    { titulo: 'Nome', label: 'name' },
    { titulo: 'CPF', label: 'driverCpf', formatter: (value: any) => this.formatCpfCnpj(value) },
    { titulo: 'CNH', label: 'driverCnh' },
    { titulo: 'Matrícula', label: 'employeeRegistration' },
    { titulo: 'Telefone', label: 'phone', formatter: (value: any) => this.formatPhone(value) },
    { titulo: 'Endereço', label: 'address' }
  ];

  vehicleColumns: TableColumn[] = [
    { titulo: 'Placa', label: 'plate' },
    { titulo: 'Modelo', label: 'model' },
    { titulo: 'Ano', label: 'year' },
    { titulo: 'Status', label: 'operationalStatus' },
    { titulo: 'Rastreador', label: 'trackerType' },
    { titulo: 'Pedido', label: 'itemNumber' }
  ];

  vehicleActions: TableAction[] = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'viewVehicle',
      class: 'btn-view'
    }
  ];

  driverActions: TableAction[] = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view'
    },
    {
      label: 'Editar',
      icon: 'fas fa-edit',
      action: 'edit',
      class: 'btn-edit'
    },
    {
      label: 'Cancelar',
      icon: 'fas fa-trash',
      action: 'delete',
      class: 'btn-delete'
    }
  ];

  itemsActions: TableAction[] = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view'
    },
    {
      label: 'Editar',
      icon: 'fas fa-edit',
      action: 'edit',
      class: 'btn-edit'
    }
  ];

  isSubmitting = false;

  /** Cancelamento de contrato (mesmo fluxo da lista B2B + contrato padrão: Asaas PENDING) */
  showCancelContractModal = false;
  cancelReasonCode: ContractCancelReason | '' = '';
  isCancellingContract = false;
  readonly contractCancelReasonOptions = CONTRACT_CANCEL_REASON_SELECT_OPTIONS;
  
  // Modal de visualizar item
  showViewItemModal = false;
  selectedItem: any = null;
  isLoadingItem = false;
  
  // Modal de editar item
  showEditItemModal = false;
  editItemForm: FormGroup;
  editingItemId: number | null = null;
  private editDeliveryTypeSub?: Subscription;

  // Opções para os selects
  paymentTypeOptions = ['Mensal', 'Trimestral', 'Semestral', 'Anual'];
  contractTypeOptions = ['Locação', 'Venda', 'Comodato'];
  deliveryTypeOptions = ['Retirada', 'Entrega'];
  statusOptions = ['Active', 'Inactive', 'Pending'];
  
  // Ano atual para validação
  currentYear = new Date().getFullYear();
  maxYear = this.currentYear + 1;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contractsService: ContractsService,
    private paymentService: PaymentService,
    private accountsReceivableService: AccountsReceivableService,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {
    this.editItemForm = this.createEditItemForm();
  }

  ngOnInit(): void {
    this.applyEditItemDeliveryAddressValidators(this.editItemForm.get('deliveryType')?.value);
    this.editDeliveryTypeSub = this.editItemForm.get('deliveryType')?.valueChanges.subscribe((v) => {
      this.applyEditItemDeliveryAddressValidators(v);
    });

    this.route.params.subscribe(params => {
      if (params['id']) {
        this.contractId = +params['id'];
        this.loadContract();
      }
    });
  }

  ngOnDestroy(): void {
    this.editDeliveryTypeSub?.unsubscribe();
    this.destroyDocumentEditor();
  }

  /** Endereço só obrigatório para entrega no local do cliente (modal editar item). */
  get isEditEntregaDelivery(): boolean {
    return this.editItemForm.get('deliveryType')?.value === 'Entrega';
  }

  private applyEditItemDeliveryAddressValidators(deliveryType: string | null | undefined): void {
    const state = this.editItemForm.get('state');
    const city = this.editItemForm.get('city');
    const pole = this.editItemForm.get('pole');
    if (!state || !city || !pole) return;

    if (deliveryType === 'Entrega') {
      state.setValidators([Validators.required]);
      city.setValidators([Validators.required]);
      pole.setValidators([Validators.required]);
    } else {
      state.clearValidators();
      city.clearValidators();
      pole.clearValidators();
      state.setValue('', { emitEvent: false });
      city.setValue('', { emitEvent: false });
      pole.setValue('', { emitEvent: false });
    }
    state.updateValueAndValidity({ emitEvent: false });
    city.updateValueAndValidity({ emitEvent: false });
    pole.updateValueAndValidity({ emitEvent: false });
  }

  loadContract(): void {
    if (!this.contractId) return;

    this.isLoading = true;
    // eslint-disable-next-line no-console
    
    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (contract) => {
        // eslint-disable-next-line no-console
        // eslint-disable-next-line no-console
        
        if (contract) {
          // Atribuir o contrato
          this.contract = { ...contract }; // Criar uma nova referência para forçar detecção de mudanças
          // Carregar itens e condutores do contrato para exibir nas listas (sempre ativos na visualização)
          this.loadItems();
          this.loadDrivers();
          this.loadVehicles();
          this.loadReceivables();
          // Forçar detecção de mudanças
          this.cdr.detectChanges();
        } else {
          this.errorService.showErrorMessage('Contrato não encontrado ou inválido');
        }
        this.isLoading = false;
      },
      error: (err) => {
        // eslint-disable-next-line no-console
        console.error('[B2bContractsView] Erro ao carregar contrato:', err);
        this.errorService.handleHttpError(err);
        this.isLoading = false;
        this.contract = null;
      }
    });
  }

  navigateBack(): void {
    this.router.navigate(['/b2b/contracts']);
  }

  navigateToEdit(): void {
    if (this.contractId) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'edit']);
    }
  }

  navigateDepositRefund(): void {
    if (this.contractId) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'deposit-refund']);
    }
  }

  canCancelB2bContract(): boolean {
    return !!this.contract && this.contract.status !== 'Cancelled';
  }

  openCancelContractModal(): void {
    if (!this.canCancelB2bContract()) return;
    this.cancelReasonCode = '';
    this.showCancelContractModal = true;
  }

  closeCancelContractModal(): void {
    this.showCancelContractModal = false;
    this.cancelReasonCode = '';
  }

  confirmCancelContract(): void {
    if (!this.contractId || !this.contract) return;
    const motivo = contractCancelMotivoFromCode(this.cancelReasonCode);
    if (!motivo) {
      this.errorService.showErrorMessage('Selecione o motivo do cancelamento.');
      return;
    }
    const contractId = this.contractId;
    const contractRef = this.contract;

    this.isCancellingContract = true;
    this.contractsService.cancelB2bContract(contractId, motivo).subscribe({
      next: (response) => {
        const baseMessage = response?.message || 'Contrato B2B cancelado com sucesso!';

        const runAsaasCleanup = (cnpjSource: string) => {
          const digits = (cnpjSource || '').replace(/\D/g, '');
          if (!digits || (digits.length !== 11 && digits.length !== 14)) {
            this.errorService.showInfoMessage(baseMessage);
            this.closeCancelContractModal();
            this.isCancellingContract = false;
            this.loadContract();
            return;
          }

          this.paymentService.cancelPendingAsaasChargesAfterContractCancel(digits).subscribe({
            next: ({ hasOverdue, customerFound }) => {
              if (!customerFound) {
                this.errorService.showInfoMessage(baseMessage + ' (cliente Asaas não encontrado).');
              } else if (hasOverdue) {
                this.errorService.showInfoMessage(
                  baseMessage + ' Contrato cancelado com pendência financeira (boletos vencidos mantidos no Asaas).'
                );
              } else {
                this.errorService.showInfoMessage(baseMessage + ' Boletos pendentes cancelados no Asaas.');
              }
              this.closeCancelContractModal();
              this.isCancellingContract = false;
              this.loadContract();
            },
            error: (err3) => {
              this.errorService.showErrorMessage(
                `Contrato cancelado, mas houve erro ao processar cobranças no Asaas: ${err3?.message || err3}`
              );
              this.closeCancelContractModal();
              this.isCancellingContract = false;
              this.loadContract();
            }
          });
        };

        this.contractsService.getB2bContractById(contractId).subscribe({
          next: (detail) => {
            const cnpj = (detail?.cnpj ?? contractRef.cnpj ?? '').toString();
            runAsaasCleanup(cnpj);
          },
          error: () => {
            runAsaasCleanup((contractRef.cnpj || '').toString());
          }
        });
      },
      error: (err) => {
        this.errorService.showErrorMessage(`Erro ao cancelar contrato B2B: ${err?.message || err}`);
        this.isCancellingContract = false;
        this.closeCancelContractModal();
      }
    });
  }

  navigateToDriverAssignment(): void {
    if (this.contractId) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'central-controle']);
    }
  }

  navigateToDriversList(): void {
    if (this.contractId) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'drivers']);
    }
  }

  navigateToItemsList(): void {
    if (this.contractId) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'items']);
    }
  }

  navigateToVehiclesList(): void {
    if (this.contractId) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'vehicles']);
    }
  }

  navigateOperational(section: 'operacional' | 'sinistros' | 'cobrancas' | 'faturas' | 'reservas' | 'acessorios' | 'pagamentos' | 'manutencoes' | 'multas' | 'substituicoes' | 'retornos' | 'entregas'): void {
    if (!this.contractId) return;
    this.router.navigate(['/b2b/contracts', this.contractId, section]);
  }

  onManagementAction(item: {
    type: 'scroll' | 'route' | 'action';
    target?: string;
  }): void {
    if (!item?.target) return;

    if (item.type === 'scroll') {
      this.scrollToSection(item.target);
      return;
    }

    if (item.type === 'action') {
      if (item.target === 'show-items') {
        this.showItemsTable = true;
        setTimeout(() => this.scrollToSection('pedidos-contrato'), 80);
        return;
      }
      if (item.target === 'deposit-refund') {
        this.navigateDepositRefund();
        return;
      }
      if (item.target === 'billing-closing') {
        if (!this.contractId) return;
        this.router.navigate(['/b2b/billing-closing'], {
          queryParams: { contractId: this.contractId, wizard: '1' }
        });
        return;
      }
      return;
    }

    if (!this.contractId) return;
    this.router.navigate(['/b2b/contracts', this.contractId, item.target]);
  }

  scrollToSection(sectionId: string): void {
    const el = document.getElementById(sectionId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  navigateToNewDriver(): void {
    if (this.contractId) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'drivers', 'new']);
    }
  }

  viewDriver(driverId: number): void {
    if (!this.contractId) return;
    this.router.navigate(['/b2b/contracts', this.contractId, 'drivers', driverId, 'view']);
  }

  editDriver(driverId: number): void {
    if (!this.contractId) return;
    this.router.navigate(['/b2b/contracts', this.contractId, 'drivers', driverId, 'edit']);
  }

  deleteDriver(driverId: number, driverName: string): void {
    if (!this.contractId) return;

    this.confirmationService.confirmDelete(driverName || 'este condutor', 'o condutor').subscribe({
      next: (confirmed: boolean) => {
        if (confirmed) {
          this.contractsService.deleteB2bContractDriver(this.contractId!, driverId).subscribe({
            next: () => {
              this.errorService.showSuccessMessage('Condutor excluído com sucesso!');
              this.loadDrivers();
              this.loadContract();
            },
            error: (err) => {
              this.errorService.handleHttpError(err);
            }
          });
        }
      }
    });
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  }

  formatCurrency(value: number): string {
    if (!value && value !== 0) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  formatCnpj(cnpj: string | undefined): string {
    if (!cnpj) return 'N/A';
    // Se já está formatado, retorna como está
    if (cnpj.includes('/') || cnpj.includes('-')) {
      return cnpj;
    }
    const digits = String(cnpj).replace(/\D/g, '');
    if (digits.length === 14) {
      return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }
    return cnpj;
  }

  formatDateTime(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  }

  loadReceivables(): void {
    if (!this.contractId) return;
    this.isLoadingReceivables = true;
    const saleOrderNumber = `B2B-CONTRATO-${this.contractId}`;
    this.accountsReceivableService.getBySaleOrderNumber(saleOrderNumber, { page: 1, pageSize: 200 }).subscribe({
      next: (response) => {
        const rows = Array.isArray(response?.items) ? response.items : [];
        this.receivables = rows
          .map((row) => ({
            ...row,
            installmentLabel:
              row?.installmentNumber && row?.totalInstallments
                ? `${row.installmentNumber}/${row.totalInstallments}`
                : '-'
          }))
          .sort((a, b) => String(a?.dueDate || '').localeCompare(String(b?.dueDate || '')));
        this.receivablesTotal = this.receivables.length;
        this.isLoadingReceivables = false;
      },
      error: (err) => {
        this.receivables = [];
        this.receivablesTotal = 0;
        this.isLoadingReceivables = false;
        this.errorService.handleHttpError(err);
      }
    });
  }

  getStatusBadgeClass(status: string | undefined): string {
    if (!status) return 'badge-secondary';
    const statusLower = status.toLowerCase();
    if (statusLower === 'active' || statusLower === 'ativo') return 'badge-success';
    if (statusLower === 'inactive' || statusLower === 'inativo') return 'badge-danger';
    if (statusLower === 'draft' || statusLower === 'rascunho') return 'badge-warning';
    if (statusLower === 'pending' || statusLower === 'pendente') return 'badge-warning';
    if (statusLower === 'cancelled' || statusLower === 'cancelado') return 'badge-danger';
    return 'badge-secondary';
  }

  getStatusLabel(status: string | undefined): string {
    if (!status) return 'N/A';
    const statusMap: { [key: string]: string } = {
      'Aguardando Assinatura': 'Aguardando Assinatura',
      'Assinado': 'Assinado',
      'Ativo': 'Ativo',
      'Vigente': 'Vigente',
      'Expirado': 'Expirado',
      'Cancelado': 'Cancelado',
      'Rascunho': 'Rascunho',
      'Pending': 'Pendente',
      'Signed': 'Assinado',
      'Active': 'Ativo',
      'Inactive': 'Inativo',
      'Expired': 'Expirado',
      'Cancelled': 'Cancelado',
      'Draft': 'Rascunho'
    };
    return statusMap[status] || status || 'N/A';
  }

  getTotalVehicles(): number {
    if (this.totalVehicles > 0) return this.totalVehicles;
    if (!this.contract) return 0;
    const rootVehicles = (this.contract as any).vehicles || (this.contract as any).Vehicles || [];
    if (Array.isArray(rootVehicles) && rootVehicles.length > 0) return rootVehicles.length;
    const items = this.contract.contractItems || [];
    let total = 0;
    items.forEach((item: any) => {
      const vehicles = item.vehicleSnapshots || item.VehicleSnapshots || [];
      total += vehicles.length;
    });
    return total;
  }

  getTotalDriversDisplay(): number {
    return this.totalDrivers > 0 ? this.totalDrivers : (this.contract?.drivers || []).length;
  }

  getStatusClass(status: string | undefined): string {
    if (!status) return 'badge-info';
    const statusUpper = (status || '').toUpperCase();
    if (statusUpper.includes('AGUARDANDO') || statusUpper.includes('PENDING')) {
      return 'badge-warning';
    }
    if (statusUpper.includes('ASSINADO') || statusUpper.includes('SIGNED')) {
      return 'badge-success';
    }
    if (statusUpper.includes('ATIVO') || statusUpper.includes('ACTIVE') || statusUpper.includes('VIGENTE')) {
      return 'badge-success';
    }
    if (statusUpper.includes('EXPIRADO') || statusUpper.includes('EXPIRED')) {
      return 'badge-danger';
    }
    if (statusUpper.includes('CANCELADO') || statusUpper.includes('CANCELLED')) {
      return 'badge-danger';
    }
    if (statusUpper.includes('RASCUNHO') || statusUpper.includes('DRAFT')) {
      return 'badge-secondary';
    }
    return 'badge-info';
  }

  getItemStatusClass(status: string): string {
    const statusUpper = (status || '').toUpperCase();
    if (statusUpper.includes('ACTIVE') || statusUpper.includes('ATIVO')) {
      return 'badge-success';
    }
    if (statusUpper.includes('INACTIVE') || statusUpper.includes('INATIVO')) {
      return 'badge-secondary';
    }
    return 'badge-info';
  }

  // ===== UTILITÁRIOS PARA CONDUTORES =====

  private formatCpfCnpj(value: string): string {
    if (!value) return '-';
    const digits = String(value).replace(/\D/g, '');
    if (digits.length === 11) {
      return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    }
    if (digits.length === 14) {
      return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }
    return value;
  }

  private formatPhone(value: string): string {
    if (!value) return '-';
    const digits = String(value).replace(/\D/g, '');
    if (digits.length === 10) {
      return digits.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    }
    if (digits.length === 11) {
      return digits.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
    }
    return value;
  }

  async downloadDocument(url: string | null): Promise<void> {
    if (!url) {
      this.errorService.showErrorMessage('Documento não disponível');
      return;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Falha no download (${response.status})`);
      }

      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        throw new Error('Arquivo retornou vazio.');
      }

      const fileName = this.extractFileNameFromUrl(url, response.headers.get('content-disposition'));
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      // Fallback para não bloquear o usuário quando houver restrição do blob/download
      window.open(url, '_blank');
      this.errorService.showInfoMessage('Não foi possível baixar diretamente; abrindo documento em nova aba.');
    }
  }

  private extractFileNameFromUrl(url: string, contentDisposition?: string | null): string {
    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename\*?=([^;]+)/i);
      if (fileNameMatch?.[1]) {
        const raw = fileNameMatch[1].trim().replace(/^UTF-8''/i, '').replace(/"/g, '');
        try {
          return decodeURIComponent(raw);
        } catch {
          return raw;
        }
      }
    }

    try {
      const parsed = new URL(url);
      const path = parsed.pathname.split('/').filter(Boolean);
      const last = path[path.length - 1];
      return last || 'documento';
    } catch {
      return 'documento';
    }
  }

  get currentB2bDocumentId(): number | null {
    const contractAny = this.contract as any;
    const directId = contractAny?.contractDocumentId
      ?? contractAny?.documentId
      ?? contractAny?.ContractDocumentId
      ?? contractAny?.DocumentId;

    const normalizedDirectId = Number(directId);
    if (Number.isFinite(normalizedDirectId) && normalizedDirectId > 0) {
      return normalizedDirectId;
    }

    const docs = contractAny?.documents ?? contractAny?.Documents ?? [];
    if (Array.isArray(docs) && docs.length > 0) {
      const pendingDoc = docs.find((d: any) => {
        const status = (d?.signatureStatus ?? d?.SignatureStatus ?? '').toString().toUpperCase();
        return status === 'PENDING' || status === 'PENDENTE' || status === '';
      }) || docs[0];
      const listId = Number(
        pendingDoc?.id
        ?? pendingDoc?.Id
        ?? pendingDoc?.documentId
        ?? pendingDoc?.DocumentId
        ?? pendingDoc?.contractDocumentId
        ?? pendingDoc?.ContractDocumentId
        ?? pendingDoc?.providerDocumentId
        ?? pendingDoc?.ProviderDocumentId
      );
      if (Number.isFinite(listId) && listId > 0) {
        return listId;
      }
    }

    const urlId = this.extractDocumentIdFromUrl(this.contract?.contractDocumentUrl || null);
    if (urlId) {
      return urlId;
    }

    // Fallback final: alguns retornos de visualizar expõem apenas o Id do contrato.
    const contractIdFallback = Number(contractAny?.id ?? contractAny?.Id);
    if (Number.isFinite(contractIdFallback) && contractIdFallback > 0) {
      return contractIdFallback;
    }

    return null;
  }

  private extractDocumentIdFromUrl(url: string | null): number | null {
    if (!url) return null;
    const match = url.match(/\/documents\/(\d+)(?:\/|$|\?)/i);
    if (!match?.[1]) return null;
    const parsed = Number(match[1]);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  viewContractDocument(): void {
    const url = this.contract?.contractDocumentUrl;
    if (!url) {
      this.errorService.showErrorMessage('Documento não disponível para visualização.');
      return;
    }
    this.selectedDocumentUrl = url;
    this.showDocumentViewer = true;
    this.isLoadingDocumentViewer = true;
    this.documentViewerError = null;
    void this.prepareDocumentViewer(url);
  }

  closeDocumentViewer(): void {
    this.showDocumentViewer = false;
    this.selectedDocumentUrl = null;
    this.isLoadingDocumentViewer = false;
    this.documentViewerError = null;
  }

  private isPublicHttpUrl(url: string): boolean {
    if (!/^https?:\/\//i.test(url)) return false;
    if (/localhost|127\.0\.0\.1|0\.0\.0\.0/i.test(url)) return false;
    if (/192\.168\.|10\.|172\.(1[6-9]|2\d|3[0-1])\./i.test(url)) return false;
    return true;
  }

  private async prepareDocumentViewer(url: string): Promise<void> {
    const cleanUrl = url.split('?')[0].split('#')[0].toLowerCase();
    const isPdf = cleanUrl.endsWith('.pdf');
    const isDocx = cleanUrl.endsWith('.docx') || cleanUrl.endsWith('.doc');

    if (isPdf) {
      this.documentViewerType = 'iframe';
      this.documentViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      this.isLoadingDocumentViewer = false;
      return;
    }

    if (isDocx && !this.isPublicHttpUrl(url)) {
      const rendered = await this.tryRenderDocxInModal(url);
      if (rendered) {
        this.documentViewerType = 'docx';
        this.isLoadingDocumentViewer = false;
        return;
      }
    }

    if (isDocx && this.isPublicHttpUrl(url)) {
      const encodedUrl = encodeURIComponent(url);
      const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
      this.documentViewerType = 'iframe';
      this.documentViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl);
      this.isLoadingDocumentViewer = false;
      return;
    }

    this.documentViewerType = 'iframe';
    this.documentViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.isLoadingDocumentViewer = false;
  }

  private async tryRenderDocxInModal(url: string): Promise<boolean> {
    try {
      await new Promise(resolve => setTimeout(resolve, 0));
      const container = this.documentViewerDocxContainer?.nativeElement;
      if (!container) return false;
      container.innerHTML = '';
      const response = await fetch(url);
      if (!response.ok) return false;
      const blob = await response.blob();
      const docxPreview = await import('docx-preview');
      await docxPreview.renderAsync(blob, container, undefined, {
        className: 'docx',
        inWrapper: true,
        breakPages: true
      });
      return true;
    } catch {
      return false;
    }
  }

  openSelectedDocumentInNewTab(): void {
    if (!this.selectedDocumentUrl) return;
    window.open(this.selectedDocumentUrl, '_blank');
  }

  openB2bSignModal(): void {
    this.showB2bSignModal = true;
    this.isDrawingB2bSignature = false;
    setTimeout(() => this.initializeB2bSignatureCanvas(), 120);
  }

  closeB2bSignModal(): void {
    this.showB2bSignModal = false;
  }

  private initializeB2bSignatureCanvas(): void {
    if (!this.b2bSignatureCanvasRef) return;
    this.b2bSignatureCanvas = this.b2bSignatureCanvasRef.nativeElement;
    const ctx = this.b2bSignatureCanvas.getContext('2d');
    if (!ctx) return;
    this.b2bSignatureCanvas.width = 520;
    this.b2bSignatureCanvas.height = 180;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, this.b2bSignatureCanvas.width, this.b2bSignatureCanvas.height);
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2.6;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    this.b2bSignatureCanvas.onmousedown = (e: MouseEvent) => this.startB2bSignatureDrawing(e);
    this.b2bSignatureCanvas.onmousemove = (e: MouseEvent) => this.drawB2bSignature(e);
    this.b2bSignatureCanvas.onmouseup = () => this.stopB2bSignatureDrawing();
    this.b2bSignatureCanvas.onmouseleave = () => this.stopB2bSignatureDrawing();
    this.b2bSignatureCanvas.ontouchstart = (e: TouchEvent) => { e.preventDefault(); this.startB2bSignatureDrawing(e); };
    this.b2bSignatureCanvas.ontouchmove = (e: TouchEvent) => { e.preventDefault(); this.drawB2bSignature(e); };
    this.b2bSignatureCanvas.ontouchend = (e: TouchEvent) => { e.preventDefault(); this.stopB2bSignatureDrawing(); };
  }

  private startB2bSignatureDrawing(event: MouseEvent | TouchEvent): void {
    if (!this.b2bSignatureCanvas) return;
    const ctx = this.b2bSignatureCanvas.getContext('2d');
    if (!ctx) return;
    this.isDrawingB2bSignature = true;
    const p = this.getB2bSignaturePoint(event);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  }

  private drawB2bSignature(event: MouseEvent | TouchEvent): void {
    if (!this.isDrawingB2bSignature || !this.b2bSignatureCanvas) return;
    const ctx = this.b2bSignatureCanvas.getContext('2d');
    if (!ctx) return;
    const p = this.getB2bSignaturePoint(event);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  }

  private stopB2bSignatureDrawing(): void {
    this.isDrawingB2bSignature = false;
  }

  private getB2bSignaturePoint(event: MouseEvent | TouchEvent): { x: number; y: number } {
    if (!this.b2bSignatureCanvas) return { x: 0, y: 0 };
    const rect = this.b2bSignatureCanvas.getBoundingClientRect();
    const scaleX = this.b2bSignatureCanvas.width / rect.width;
    const scaleY = this.b2bSignatureCanvas.height / rect.height;
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  }

  clearB2bSignatureCanvas(): void {
    if (!this.b2bSignatureCanvas) return;
    const ctx = this.b2bSignatureCanvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, this.b2bSignatureCanvas.width, this.b2bSignatureCanvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, this.b2bSignatureCanvas.width, this.b2bSignatureCanvas.height);
  }

  signCurrentB2bDocument(): void {
    const documentId = this.currentB2bDocumentId;

    if (!this.b2bSignatureCanvas && this.b2bSignatureCanvasRef?.nativeElement) {
      this.b2bSignatureCanvas = this.b2bSignatureCanvasRef.nativeElement;
    }

    if (!documentId || !this.b2bSignatureCanvas) {
      this.errorService.showErrorMessage('Não foi possível identificar o documento para assinatura. Recarregue a tela e tente novamente.');
      return;
    }
    const dataUrl = this.b2bSignatureCanvas.toDataURL('image/png');
    const base64Signature = dataUrl.split(',')[1] || '';
    if (!base64Signature) {
      this.errorService.showErrorMessage('Desenhe a assinatura antes de enviar.');
      return;
    }
    this.isSigningB2bDocument = true;
    this.contractsService.signB2bDocument(documentId, base64Signature).subscribe({
      next: () => {
        this.errorService.showSuccessMessage('Documento assinado e nova versão salva com sucesso.');
        this.closeB2bSignModal();
        this.loadContract();
      },
      error: () => {
        this.isSigningB2bDocument = false;
      },
      complete: () => {
        this.isSigningB2bDocument = false;
      }
    });
  }

  // ===== DOCUMENTO DO CONTRATO B2B =====
  selectedDocumentFile: File | null = null;
  selectedSignatureReferenceImage: File | null = null;
  signaturePositionHint = '';
  signaturePositionHintOptions: string[] = [
    'Rodapé - canto inferior direito',
    'Rodapé - canto inferior esquerdo',
    'Última página - área de assinatura do contratante',
    'Última página - área de assinatura da contratada',
    'Próximo ao texto "Assinatura"'
  ];
  isUploadingDocument = false;
  documentInputId = 'b2b-contract-document-input';
  signatureReferenceInputId = 'b2b-contract-signature-reference-input';
  showSignaturePositionModal = false;
  signatureModalMode: 'pdf' | 'docx' | null = null;
  isRenderingSignaturePreview = false;
  isApplyingSignatureStamp = false;
  signaturePreviewError: string | null = null;
  signatureSizeWidth = 190;
  signatureSizeHeight = 60;
  selectedSignaturePosition: { page: number; x: number; y: number; xPct: number; yPct: number } | null = null;
  signatureMarkerStyle: { left: string; top: string } | null = null;
  showDocumentEditorModal = false;
  isLoadingDocumentEditor = false;
  isSavingDocumentEditor = false;
  documentEditorError: string | null = null;
  private documentEditorInited = false;
  private documentEditorHtml = '';
  showDocumentViewer = false;
  selectedDocumentUrl: string | null = null;
  documentViewerType: 'iframe' | 'docx' = 'iframe';
  documentViewerUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl('');
  isLoadingDocumentViewer = false;
  documentViewerError: string | null = null;
  showB2bSignModal = false;
  isDrawingB2bSignature = false;
  isSigningB2bDocument = false;
  private b2bSignatureCanvas: HTMLCanvasElement | null = null;

  onDocumentFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (file) {
      this.selectedDocumentFile = file;
      this.selectedSignaturePosition = null;
      this.signatureMarkerStyle = null;
      this.cdr.detectChanges();
    }
    input.value = '';
  }

  clearSelectedDocument(): void {
    this.selectedDocumentFile = null;
    this.selectedSignaturePosition = null;
    this.signatureMarkerStyle = null;
    this.cdr.detectChanges();
  }

  openDocumentEditor(): void {
    if (!this.selectedDocumentFile) {
      this.errorService.showErrorMessage('Selecione um documento para editar.');
      return;
    }
    if (!this.isDocxSelected) {
      this.errorService.showInfoMessage('A edição em tela está disponível apenas para arquivos DOCX.');
      return;
    }
    this.showDocumentEditorModal = true;
    this.isLoadingDocumentEditor = true;
    this.documentEditorError = null;
    void this.loadSelectedDocxAsHtml();
  }

  closeDocumentEditor(): void {
    this.showDocumentEditorModal = false;
    this.isLoadingDocumentEditor = false;
    this.isSavingDocumentEditor = false;
    this.documentEditorError = null;
    this.documentEditorHtml = '';
    this.destroyDocumentEditor();
  }

  onSignatureReferenceImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (file) {
      this.selectedSignatureReferenceImage = file;
      this.cdr.detectChanges();
    }
    input.value = '';
  }

  clearSelectedSignatureReferenceImage(): void {
    this.selectedSignatureReferenceImage = null;
    this.cdr.detectChanges();
  }

  openSignaturePositionModal(): void {
    if (!this.selectedDocumentFile) {
      this.errorService.showErrorMessage('Selecione um contrato antes de marcar a assinatura.');
      return;
    }
    const isPdf = this.selectedDocumentFile.type === 'application/pdf' || this.selectedDocumentFile.name.toLowerCase().endsWith('.pdf');
    const isDocx = this.isDocxSelected;
    if (!isPdf && !isDocx) {
      this.errorService.showErrorMessage('A marcação manual está disponível para PDF e DOCX.');
      return;
    }

    this.showSignaturePositionModal = true;
    this.signatureModalMode = isPdf ? 'pdf' : 'docx';
    this.signaturePreviewError = null;
    this.isRenderingSignaturePreview = true;
    setTimeout(() => {
      if (this.signatureModalMode === 'pdf') {
        void this.renderSignaturePdfPreview();
        return;
      }
      void this.renderSignatureDocxPreview();
    }, 0);
  }

  closeSignaturePositionModal(): void {
    this.showSignaturePositionModal = false;
    this.signatureModalMode = null;
    this.isRenderingSignaturePreview = false;
    this.signaturePreviewError = null;
  }

  private async renderSignaturePdfPreview(): Promise<void> {
    try {
      const file = this.selectedDocumentFile;
      const canvas = this.signaturePdfCanvas?.nativeElement;
      if (!file || !canvas) return;

      const pdfjsLib: any = await import('pdfjs-dist');
      const data = await file.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ data, disableWorker: true });
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 1.3 });

      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Canvas não disponível.');

      await page.render({ canvasContext: context, viewport }).promise;
      this.isRenderingSignaturePreview = false;
      this.cdr.detectChanges();
    } catch (error: any) {
      this.isRenderingSignaturePreview = false;
      this.signaturePreviewError = error?.message || 'Não foi possível renderizar o PDF para marcação.';
      this.cdr.detectChanges();
    }
  }

  onSignatureCanvasClick(event: MouseEvent): void {
    const canvas = this.signaturePdfCanvas?.nativeElement;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
    const y = Math.max(0, Math.min(rect.height, event.clientY - rect.top));

    const xPct = rect.width > 0 ? x / rect.width : 0;
    const yPct = rect.height > 0 ? y / rect.height : 0;
    this.selectedSignaturePosition = {
      page: 1,
      x: Math.round(x),
      y: Math.round(y),
      xPct: Number(xPct.toFixed(6)),
      yPct: Number(yPct.toFixed(6))
    };
    this.signatureMarkerStyle = {
      left: `${x}px`,
      top: `${y}px`
    };
  }

  private async renderSignatureDocxPreview(): Promise<void> {
    try {
      const file = this.selectedDocumentFile;
      const container = this.docxPreviewContainer?.nativeElement;
      if (!file || !container) return;

      container.innerHTML = '';
      const docxPreview = await import('docx-preview');
      await docxPreview.renderAsync(file, container, undefined, {
        className: 'docx',
        inWrapper: true,
        breakPages: true
      });

      this.isRenderingSignaturePreview = false;
      this.cdr.detectChanges();
    } catch (error: any) {
      this.isRenderingSignaturePreview = false;
      this.signaturePreviewError = error?.message || 'Não foi possível renderizar o DOCX para marcação.';
      this.cdr.detectChanges();
    }
  }

  onDocxPreviewClick(event: MouseEvent): void {
    const container = this.docxPreviewContainer?.nativeElement;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const xWithScroll = (event.clientX - rect.left) + container.scrollLeft;
    const yWithScroll = (event.clientY - rect.top) + container.scrollTop;
    const fullWidth = container.scrollWidth || rect.width;
    const fullHeight = container.scrollHeight || rect.height;
    const x = Math.max(0, Math.min(fullWidth, xWithScroll));
    const y = Math.max(0, Math.min(fullHeight, yWithScroll));
    const xPct = fullWidth > 0 ? x / fullWidth : 0;
    const yPct = fullHeight > 0 ? y / fullHeight : 0;

    this.selectedSignaturePosition = {
      page: 1,
      x: Math.round(x),
      y: Math.round(y),
      xPct: Number(xPct.toFixed(6)),
      yPct: Number(yPct.toFixed(6))
    };
    this.signatureMarkerStyle = {
      left: `${x}px`,
      top: `${y}px`
    };
  }

  get isDocxSelected(): boolean {
    const file = this.selectedDocumentFile;
    if (!file) return false;
    const name = file.name.toLowerCase();
    return name.endsWith('.docx') || file.type.includes('wordprocessingml.document');
  }

  async applySignatureStampToDocument(): Promise<void> {
    if (!this.selectedDocumentFile || !this.selectedSignaturePosition) {
      this.errorService.showErrorMessage('Selecione um documento PDF e marque a posição da assinatura.');
      return;
    }

    const isPdf = this.selectedDocumentFile.type === 'application/pdf' || this.selectedDocumentFile.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      this.errorService.showErrorMessage('A aplicação da assinatura no documento está disponível apenas para PDF.');
      return;
    }

    this.isApplyingSignatureStamp = true;
    try {
      const pdfBytes = await this.selectedDocumentFile.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pageNumber = this.selectedSignaturePosition.page || 1;
      const page = pdfDoc.getPage(Math.max(0, pageNumber - 1));
      const pageWidth = page.getWidth();
      const pageHeight = page.getHeight();

      const stampPngBytes = await this.createSignatureStampPngBytes();
      const stampImage = await pdfDoc.embedPng(stampPngBytes);
      const stampWidth = this.signatureSizeWidth;
      const stampHeight = (stampImage.height / stampImage.width) * stampWidth;

      const x = Math.max(0, Math.min(pageWidth - stampWidth, this.selectedSignaturePosition.xPct * pageWidth));
      const yFromTop = this.selectedSignaturePosition.yPct * pageHeight;
      const y = Math.max(0, Math.min(pageHeight - stampHeight, pageHeight - yFromTop - stampHeight));

      page.drawImage(stampImage, {
        x,
        y,
        width: stampWidth,
        height: stampHeight
      });

      const updatedBytes = await pdfDoc.save();
      const updatedArrayBuffer = updatedBytes.buffer.slice(
        updatedBytes.byteOffset,
        updatedBytes.byteOffset + updatedBytes.byteLength
      ) as ArrayBuffer;
      const baseName = this.selectedDocumentFile.name.replace(/\.pdf$/i, '');
      const stampedFile = new File([updatedArrayBuffer], `${baseName}-assinatura-cliente.pdf`, { type: 'application/pdf' });
      this.selectedDocumentFile = stampedFile;
      this.signaturePositionHint = this.signaturePositionHint || 'Assinatura do cliente aplicada no documento';
      this.closeSignaturePositionModal();
      this.errorService.showSuccessMessage('Assinatura do cliente aplicada no documento. Agora basta clicar em "Enviar documento".');
    } catch (error: any) {
      this.errorService.showErrorMessage(error?.message || 'Não foi possível aplicar a assinatura no documento.');
    } finally {
      this.isApplyingSignatureStamp = false;
      this.cdr.detectChanges();
    }
  }

  private async createSignatureStampPngBytes(): Promise<Uint8Array> {
    const canvas = document.createElement('canvas');
    canvas.width = 760;
    canvas.height = 220;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Falha ao criar imagem de assinatura.');
    }

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 4;
    ctx.strokeRect(8, 8, canvas.width - 16, canvas.height - 16);

    ctx.fillStyle = '#111827';
    ctx.font = 'bold 52px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('ASSINATURA DO CLIENTE', canvas.width / 2, 95);

    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(70, 155);
    ctx.lineTo(canvas.width - 70, 155);
    ctx.stroke();

    ctx.fillStyle = '#4b5563';
    ctx.font = '28px Arial';
    ctx.fillText('Assinar no local indicado', canvas.width / 2, 195);

    const dataUrl = canvas.toDataURL('image/png');
    const res = await fetch(dataUrl);
    const arr = await res.arrayBuffer();
    return new Uint8Array(arr);
  }

  private async loadSelectedDocxAsHtml(): Promise<void> {
    try {
      if (!this.selectedDocumentFile) return;
      const w: any = window as any;
      const mammothLib = w.mammoth;
      if (!mammothLib) {
        this.documentEditorError = 'Editor de documento não carregado. Recarregue a página.';
        this.isLoadingDocumentEditor = false;
        return;
      }
      const arrayBuffer = await this.selectedDocumentFile.arrayBuffer();
      const result = await mammothLib.convertToHtml({ arrayBuffer });
      let html = (result?.value || '').trim();
      if (!html) {
        html = await this.convertDocxWithPreview(this.selectedDocumentFile);
      }
      if (!html) {
        this.documentEditorError = 'Não foi possível converter este DOCX para edição.';
        this.isLoadingDocumentEditor = false;
        return;
      }
      this.documentEditorHtml = html;
      this.initDocumentEditor();
      this.isLoadingDocumentEditor = false;
      this.cdr.detectChanges();
    } catch (error: any) {
      try {
        if (this.selectedDocumentFile) {
          const fallbackHtml = await this.convertDocxWithPreview(this.selectedDocumentFile);
          if (fallbackHtml) {
            this.documentEditorHtml = fallbackHtml;
            this.initDocumentEditor();
            this.isLoadingDocumentEditor = false;
            this.cdr.detectChanges();
            return;
          }
        }
      } catch {
        // segue para erro final
      }
      this.documentEditorError = error?.message || 'Erro ao abrir o DOCX para edição.';
      this.isLoadingDocumentEditor = false;
      this.cdr.detectChanges();
    }
  }

  private async convertDocxWithPreview(file: File): Promise<string> {
    try {
      const docxPreview = await import('docx-preview');
      const tempContainer = document.createElement('div');
      tempContainer.style.visibility = 'hidden';
      tempContainer.style.position = 'fixed';
      tempContainer.style.left = '-99999px';
      document.body.appendChild(tempContainer);

      await docxPreview.renderAsync(file, tempContainer, undefined, {
        className: 'docx',
        inWrapper: true,
        breakPages: true
      });

      const html = (tempContainer.innerHTML || '').trim();
      tempContainer.remove();
      return html;
    } catch {
      return '';
    }
  }

  private initDocumentEditor(): void {
    if (this.documentEditorInited) return;
    const self = this;
    setTimeout(() => {
      const w: any = window as any;
      if (!w.tinymce) return;
      w.tinymce.get('b2bDocxEditor')?.remove();
      w.tinymce.init({
        selector: '#b2bDocxEditor',
        api_key: 'euip07pslftzv0nt86kjhzuc20huyn22dyybgpybfet1iv7s',
        height: 520,
        menubar: true,
        readonly: false,
        ui_mode: 'split',
        plugins: 'advlist autolink lists link image charmap preview searchreplace visualblocks code fullscreen insertdatetime table help wordcount',
        toolbar: 'undo redo | blocks fontfamily fontsize | bold italic underline forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist | table | removeformat | preview fullscreen',
        branding: false,
        setup(editor: any) {
          editor.on('init', () => {
            editor.setContent(self.documentEditorHtml || '');
            try {
              if (editor.mode?.set) editor.mode.set('design');
              editor.setMode?.('design');
              editor.getBody()?.setAttribute('contenteditable', 'true');
              editor.focus();
            } catch {
              // no-op: evita quebrar inicializacao se API interna variar
            }
            self.documentEditorInited = true;
          });
          editor.on('focus', () => {
            try {
              if (editor.mode?.set) editor.mode.set('design');
              editor.getBody()?.setAttribute('contenteditable', 'true');
            } catch {
              // no-op
            }
          });
        }
      });
    }, 0);
  }

  private destroyDocumentEditor(): void {
    const w: any = window as any;
    const editor = w.tinymce?.get('b2bDocxEditor');
    if (editor) editor.remove();
    this.documentEditorInited = false;
  }

  async saveDocumentEditor(): Promise<void> {
    if (!this.selectedDocumentFile || !this.isDocxSelected) return;
    this.isSavingDocumentEditor = true;
    try {
      const w: any = window as any;
      const editor = w.tinymce?.get('b2bDocxEditor');
      const currentHtml = editor ? editor.getContent({ format: 'html' }) : this.documentEditorHtml;
      const htmlDocx = w.htmlDocx;
      if (!htmlDocx) {
        this.errorService.showErrorMessage('Conversor DOCX não disponível.');
        this.isSavingDocumentEditor = false;
        return;
      }
      const fullHtml = `<!DOCTYPE html><html><head><meta charset="UTF-8"></head><body>${currentHtml || ''}</body></html>`;
      const docxBlob: Blob = htmlDocx.asBlob(fullHtml);
      const baseName = this.selectedDocumentFile.name.replace(/\.docx$/i, '');
      this.selectedDocumentFile = new File([docxBlob], `${baseName}-editado.docx`, {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      this.closeDocumentEditor();
      this.errorService.showSuccessMessage('Documento atualizado para upload.');
    } catch (error: any) {
      this.errorService.showErrorMessage(error?.message || 'Erro ao salvar o documento editado.');
    } finally {
      this.isSavingDocumentEditor = false;
      this.cdr.detectChanges();
    }
  }

  async insertDigitalSignatureAtCursor(): Promise<void> {
    try {
      const w: any = window as any;
      const editor = w.tinymce?.get('b2bDocxEditor');
      if (!editor) {
        this.errorService.showInfoMessage('Editor ainda não está pronto.');
        return;
      }

      const imageDataUrl = await this.createDigitalSignatureStampDataUrl();
      editor.focus();
      editor.insertContent(
        `<p><img src="${imageDataUrl}" alt="Assinatura Digital" style="max-width:260px;height:auto;display:inline-block;" /></p>`
      );
      this.errorService.showSuccessMessage('Imagem "Assinatura Digital" inserida no documento.');
    } catch {
      this.errorService.showErrorMessage('Não foi possível inserir a imagem de assinatura digital.');
    }
  }

  private async createDigitalSignatureStampDataUrl(): Promise<string> {
    const canvas = document.createElement('canvas');
    canvas.width = 920;
    canvas.height = 220;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas indisponível');
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 3;
    ctx.strokeRect(8, 8, canvas.width - 16, canvas.height - 16);

    ctx.fillStyle = '#111827';
    ctx.font = 'bold 64px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Assinatura Digital', canvas.width / 2, canvas.height / 2);

    return canvas.toDataURL('image/png');
  }

  uploadContractDocument(): void {
    if (!this.contractId || !this.selectedDocumentFile) {
      this.errorService.showErrorMessage('Selecione um arquivo para enviar.');
      return;
    }
    this.isUploadingDocument = true;
    this.contractsService.uploadB2bContractDocument(this.contractId, this.selectedDocumentFile, {
      signatureReferenceImage: this.selectedSignatureReferenceImage,
      signaturePositionHint: this.signaturePositionHint || null,
      signaturePosition: this.selectedSignaturePosition,
      signatureSize: this.selectedSignaturePosition
        ? { width: this.signatureSizeWidth, height: this.signatureSizeHeight }
        : null
    }).subscribe({
      next: () => {
        this.errorService.showSuccessMessage('Documento do contrato enviado com sucesso!');
        this.selectedDocumentFile = null;
        this.selectedSignatureReferenceImage = null;
        this.signaturePositionHint = '';
        this.isUploadingDocument = false;
        this.loadContract();
        this.cdr.detectChanges();
      },
      error: () => {
        this.isUploadingDocument = false;
        this.cdr.detectChanges();
      }
    });
  }

  get hasContractDocument(): boolean {
    return !!(this.contract?.contractDocumentUrl);
  }

  // ===== GESTÃO DE ITENS =====

  /** Navega para a página de cadastro de item (operação sensível em tela dedicada). */
  openAddItemModal(): void {
    if (this.contractId) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'items', 'new']);
    }
  }

  // ===== AÇÕES DOS ITENS =====

  viewItem(itemId: number): void {
    if (!this.contractId) return;
    this.router.navigate(['/b2b/contracts', this.contractId, 'items', itemId, 'view']);
  }

  closeViewItemModal(): void {
    this.showViewItemModal = false;
    this.selectedItem = null;
  }

  editItem(itemId: number): void {
    if (!this.contractId) return;
    this.router.navigate(['/b2b/contracts', this.contractId, 'items', itemId, 'edit']);
  }

  closeEditItemModal(): void {
    this.showEditItemModal = false;
    this.editingItemId = null;
    this.isSubmitting = false;
    this.editItemForm.reset();
  }

  createEditItemForm(): FormGroup {
    // Formulário de edição não inclui itemNumber e vehicleSnapshot (não editáveis)
    return this.fb.group({
      motorcycleType: ['', [Validators.required]],
      quantity: [1, [Validators.required, Validators.min(1)]],
      unitValue: [0, [Validators.required, Validators.min(0)]],
      paymentType: ['Mensal', [Validators.required]],
      installmentCount: [12, [Validators.required, Validators.min(1)]],
      state: [''],
      city: [''],
      contractType: ['Locação', [Validators.required]],
      deliveryType: ['Retirada', [Validators.required]],
      pole: [''],
      deliveryDate: ['', [Validators.required]],
      returnDate: [null],
      accessoriesDescription: [''],
      trackerType: [''],
      hasBox: [false],
      status: ['Active', [Validators.required]]
    });
  }

  onSubmitEditItem(): void {
    if (this.editItemForm.invalid || !this.contractId || !this.editingItemId) {
      this.editItemForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    const formValue = this.editItemForm.value;
    const isEntrega = formValue.deliveryType === 'Entrega';

    // Preparar dados para envio (sem itemNumber e vehicleSnapshot)
    const itemData = {
      motorcycleType: formValue.motorcycleType,
      quantity: formValue.quantity,
      unitValue: formValue.unitValue,
      paymentType: formValue.paymentType,
      installmentCount: formValue.installmentCount,
      state: isEntrega ? formValue.state : null,
      city: isEntrega ? formValue.city : null,
      contractType: formValue.contractType,
      deliveryType: formValue.deliveryType,
      pole: isEntrega ? formValue.pole : null,
      deliveryDate: formValue.deliveryDate ? new Date(formValue.deliveryDate).toISOString() : null,
      accessoriesDescription: formValue.accessoriesDescription || null,
      trackerType: formValue.trackerType || null,
      hasBox: formValue.hasBox || false,
      status: formValue.status
    };

    this.contractsService.updateB2bContractItem(this.contractId, this.editingItemId, itemData).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.errorService.showInfoMessage('Item atualizado com sucesso!');
        this.closeEditItemModal();
        this.loadContract(); // Recarregar o contrato
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        this.isSubmitting = false;
      }
    });
  }

  deactivateItem(itemId: number, itemNumber: string): void {
    if (!this.contractId) return;
    
    if (!confirm(`Tem certeza que deseja desativar o item ${itemNumber}?`)) {
      return;
    }

    this.contractsService.deactivateB2bContractItem(this.contractId, itemId).subscribe({
      next: () => {
        this.errorService.showInfoMessage('Item desativado com sucesso!');
        this.loadContract(); // Recarregar o contrato
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
      }
    });
  }

  // ===== LISTA DE CONDUTORES EMBUTIDA =====

  loadDrivers(): void {
    if (!this.contractId) return;

    this.isLoadingDrivers = true;
    this.contractsService.getB2bContractDrivers(this.contractId, this.driversPage, this.driversPageSize, this.driversSearchTerm).subscribe({
      next: (response) => {
        let driversData: any[] = [];
        if (response.data && Array.isArray(response.data)) {
          driversData = response.data;
        } else if (response.Data && Array.isArray(response.Data)) {
          driversData = response.Data;
        } else if (Array.isArray(response)) {
          driversData = response;
        }

        this.drivers = driversData.map(driver => ({
          id: driver.Id || driver.id,
          name: driver.Name || driver.name || '',
          driverCpf: driver.DriverCpf || driver.driverCpf || '',
          driverCnh: driver.DriverCnh || driver.driverCnh || '',
          phone: driver.Phone || driver.phone || '',
          email: driver.Email || driver.email || '',
          address: driver.Address || driver.address || '',
          employeeRegistration: driver.EmployeeRegistration || driver.employeeRegistration || null,
          contractNumber: driver.ContractNumber || driver.contractNumber || 'N/A',
          b2bContractId: driver.B2BContractId || driver.b2bContractId,
          termoResponsabilidadeUrl: driver.TermoResponsabilidadeUrl || driver.termoResponsabilidadeUrl,
          cnhDocumentUrl: driver.CnhDocumentUrl || driver.cnhDocumentUrl,
          itemAssociations: driver.ItemAssociations || driver.itemAssociations || [],
          createdAt: driver.CreatedAt || driver.createdAt,
          updatedAt: driver.UpdatedAt || driver.updatedAt
        }));

        if (response.pagination) {
          this.totalDrivers = response.pagination.totalItems || response.pagination.TotalItems || 0;
          this.driversPage = response.pagination.currentPage || response.pagination.CurrentPage || this.driversPage;
          this.driversTotalPages = response.pagination.totalPages || response.pagination.TotalPages
            || Math.max(1, Math.ceil(this.totalDrivers / Math.max(1, this.driversPageSize)));
        } else if (response.Pagination) {
          this.totalDrivers = response.Pagination.TotalItems || response.Pagination.totalItems || 0;
          this.driversPage = response.Pagination.CurrentPage || response.Pagination.currentPage || this.driversPage;
          this.driversTotalPages = response.Pagination.TotalPages || response.Pagination.totalPages
            || Math.max(1, Math.ceil(this.totalDrivers / Math.max(1, this.driversPageSize)));
        } else {
          this.totalDrivers = this.drivers.length;
          this.driversTotalPages = 1;
        }

        this.isLoadingDrivers = false;
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        this.isLoadingDrivers = false;
        this.drivers = [];
      }
    });
  }

  onDriversSearchChange(term: string): void {
    this.driversSearchTerm = (term || '').trim();
    this.driversPage = 1;
    this.loadDrivers();
  }

  onDriversPageChange(page: number): void {
    if (!page || page === this.driversPage) return;
    this.driversPage = page;
    this.loadDrivers();
  }

  onDriversPageSizeChange(size: number): void {
    this.driversPageSize = size > 0 ? size : 10;
    this.driversPage = 1;
    this.loadDrivers();
  }

  loadVehicles(): void {
    if (!this.contractId) return;

    this.isLoadingVehicles = true;
    this.contractsService.listB2bContractVehicles(this.contractId, this.vehiclesPage, this.vehiclesPageSize, this.vehiclesSearchTerm).subscribe({
      next: (response) => {
        const rows = Array.isArray(response?.data) ? response.data : [];
        this.vehicles = rows.map((v: any) => ({
          id: v.id || v.Id,
          plate: v.plate || v.Plate || '',
          brand: v.brand || v.Brand || '',
          model: v.model || v.Model || '',
          year: v.year || v.Year || '',
          color: v.color || v.Color || '',
          operationalStatus: v.operationalStatus || v.OperationalStatus || '',
          itemNumber: v.itemNumber || v.ItemNumber || v.b2BContractItemId || v.B2BContractItemId || '',
          trackerType: v.trackerType || v.TrackerType || '—',
          b2bContractItemId: v.b2BContractItemId || v.B2BContractItemId || v.b2bContractItemId
        }));
        const pag = response?.pagination || {};
        this.totalVehicles = pag.totalItems || pag.TotalItems || this.vehicles.length;
        this.vehiclesTotalPages = pag.totalPages || pag.TotalPages || Math.max(1, Math.ceil(this.totalVehicles / Math.max(1, this.vehiclesPageSize)));
        this.vehiclesPage = pag.currentPage || pag.CurrentPage || this.vehiclesPage;
        this.isLoadingVehicles = false;
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        this.isLoadingVehicles = false;
        this.vehicles = [];
        this.totalVehicles = 0;
        this.vehiclesTotalPages = 0;
      }
    });
  }

  onVehiclesSearchChange(term: string): void {
    this.vehiclesSearchTerm = (term || '').trim();
    this.vehiclesPage = 1;
    this.loadVehicles();
  }

  onVehiclesPageChange(page: number): void {
    if (!page || page === this.vehiclesPage) return;
    this.vehiclesPage = page;
    this.loadVehicles();
  }

  onVehiclesPageSizeChange(size: number): void {
    this.vehiclesPageSize = size > 0 ? size : 10;
    this.vehiclesPage = 1;
    this.loadVehicles();
  }

  onVehicleActionClick(actionData: { action: string; item: any }): void {
    if (actionData.action === 'viewVehicle' && actionData.item?.b2bContractItemId && this.contractId) {
      const itemId = actionData.item.b2bContractItemId;
      const snapshotId = actionData.item?.id;
      if (snapshotId) {
        this.router.navigate(['/b2b/contracts', this.contractId, 'items', itemId, 'vehicles', snapshotId, 'view']);
        return;
      }
      this.router.navigate(['/b2b/contracts', this.contractId, 'items', itemId, 'view']);
    }
  }

  onDriverActionClick(actionData: { action: string; item: any }): void {
    const { action, item } = actionData;

    switch (action) {
      case 'view':
        this.viewDriver(item.id);
        break;
      case 'edit':
        this.editDriver(item.id);
        break;
      case 'delete':
        this.deleteDriver(item.id, item.name);
        break;
      default:
        break;
    }
  }

  isEditFieldInvalid(fieldName: string): boolean {
    const field = this.editItemForm.get(fieldName);
    return !!(field && field.invalid && (field.touched || field.dirty));
  }

  getEditFieldError(fieldName: string): string {
    const field = this.editItemForm.get(fieldName);
    if (field && field.invalid && (field.touched || field.dirty)) {
      if (field.errors?.['required']) {
        return 'Campo obrigatório';
      }
      if (field.errors?.['min']) {
        return `Valor mínimo: ${field.errors['min'].min}`;
      }
    }
    return '';
  }

  // ===== GESTÃO DE VISUALIZAÇÃO DE ITENS =====

  toggleItemsView(): void {
    this.showItemsTable = !this.showItemsTable;
    if (this.showItemsTable) {
      this.loadItems();
    }
  }

  loadItems(): void {
    if (!this.contractId) return;

    this.isLoadingItems = true;

    this.contractsService.getB2bContractItems(this.contractId, this.itemsPage, this.itemsPageSize, this.itemsSearchTerm).subscribe({
      next: (response) => {
        if (response && response.data && Array.isArray(response.data)) {
          this.items = response.data;
        } else if (Array.isArray(response)) {
          this.items = response;
        } else {
          this.items = [];
        }

        const pag = response?.pagination || {};
        this.totalItems = pag.totalItems || pag.TotalItems || this.items.length;
        this.itemsTotalPages = pag.totalPages || pag.TotalPages || Math.max(1, Math.ceil(this.totalItems / Math.max(1, this.itemsPageSize)));
        this.itemsPage = pag.currentPage || pag.CurrentPage || this.itemsPage;
        this.isLoadingItems = false;
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        this.isLoadingItems = false;
        this.items = [];
        this.totalItems = 0;
        this.itemsTotalPages = 0;
      }
    });
  }

  onItemsSearchChange(term: string): void {
    this.itemsSearchTerm = (term || '').trim();
    this.itemsPage = 1;
    this.loadItems();
  }

  onItemsPageChange(page: number): void {
    if (!page || page === this.itemsPage) return;
    this.itemsPage = page;
    this.loadItems();
  }

  onItemsPageSizeChange(size: number): void {
    this.itemsPageSize = size > 0 ? size : 10;
    this.itemsPage = 1;
    this.loadItems();
  }

  onItemActionClick(actionData: { action: string; item: any }): void {
    const { action, item } = actionData;

    switch (action) {
      case 'view':
        this.viewItem(item.id);
        break;
      case 'edit':
        this.editItem(item.id);
        break;
      default:
        // eslint-disable-next-line no-console
    }
  }

  exportItemsExcel(): void {
    if (!this.contractId) return;
    const size = Math.max(this.totalItems || 0, B2B_EXPORT_PAGE_SIZE);
    this.contractsService.getB2bContractItems(this.contractId, 1, size, this.itemsSearchTerm).subscribe({
      next: (response) => {
        exportB2bTableToExcel(
          `b2b-pedidos-${this.contract?.contractNumber || this.contractId}`,
          this.itemsColumns,
          rowsFromPagedResponse(response),
          'Pedidos'
        );
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  exportDriversExcel(): void {
    if (!this.contractId) return;
    const size = Math.max(this.totalDrivers || 0, B2B_EXPORT_PAGE_SIZE);
    this.contractsService.getB2bContractDrivers(this.contractId, 1, size, this.driversSearchTerm).subscribe({
      next: (response) => {
        const rows = rowsFromPagedResponse(response).map((driver: any) => ({
          name: driver.Name || driver.name || '',
          driverCpf: driver.DriverCpf || driver.driverCpf || '',
          driverCnh: driver.DriverCnh || driver.driverCnh || '',
          employeeRegistration: driver.EmployeeRegistration || driver.employeeRegistration || null,
          phone: driver.Phone || driver.phone || '',
          address: driver.Address || driver.address || ''
        }));
        exportB2bTableToExcel(
          `b2b-condutores-${this.contract?.contractNumber || this.contractId}`,
          this.driverColumns,
          rows,
          'Condutores'
        );
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  exportVehiclesExcel(): void {
    if (!this.contractId) return;
    const size = Math.max(this.totalVehicles || 0, B2B_EXPORT_PAGE_SIZE);
    this.contractsService.listB2bContractVehicles(this.contractId, 1, size, this.vehiclesSearchTerm).subscribe({
      next: (response) => {
        const rows = rowsFromPagedResponse(response).map((v: any) => ({
          plate: v.plate || v.Plate || '',
          brand: v.brand || v.Brand || '',
          model: v.model || v.Model || '',
          year: v.year || v.Year || '',
          color: v.color || v.Color || '',
          operationalStatus: v.operationalStatus || v.OperationalStatus || '',
          trackerType: v.trackerType || v.TrackerType || '—',
          itemNumber: v.itemNumber || v.ItemNumber || v.b2BContractItemId || v.B2BContractItemId || ''
        }));
        exportB2bTableToExcel(
          `b2b-veiculos-${this.contract?.contractNumber || this.contractId}`,
          this.vehicleColumns,
          rows,
          'Veiculos'
        );
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  exportReceivablesExcel(): void {
    exportB2bTableToExcel(
      `b2b-financeiro-${this.contract?.contractNumber || this.contractId}`,
      this.receivablesColumns,
      this.receivables || [],
      'Financeiro'
    );
  }

}

